import { forwardRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Stack } from "~/common/mui";
import Chip from "~/common/components/Chip/Chip";
import Parameter, { ReverseParam } from "~/common/components/Parameter";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Price } from "~/domains/pricing/components/Price/v3/Price";
import { Product } from "~/domains/product/Product";
import { AliasesParameter, IncludedSurcharge, SchaefflerIdParameter } from "~/domains/product/components/ProductParams";
import { WeightFormat } from "~/domains/product/components/WeightFormat";
import { fakeDescriptorFromProduct, getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import {
  getPriceReferenceLabelKey,
  isPriceReferenceParamVisible,
} from "~/features/cart/cart/components/LineItemStaticParams";
import type { QuoteLineMO, QuoteLinePartMO } from "~/features/orders/common/components/v2/models";
import type { RenderCboxFn } from "~/features/orders/common/components/v2/QuoteTable/types";
import { getEDD } from "~/features/orders/common/components/v2/QuoteTable/utils";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import { QuoteCell, ProductHeaderContainer } from "./styled";
import { QuoteLineMessages } from "./QuoteLineMessages";
import { styled } from "~/common/mui/styled/styled";

// ============================================================================
// Styled Components
// ============================================================================

const Designation = styled("span")(({ theme }) => ({
  ...theme.typography.bodyLarge,
  fontWeight: "bold",
}));

const AlternativeChip = styled(Chip)({
  alignSelf: "flex-start",
});

// ============================================================================
// Component
// ============================================================================

interface ProductHeaderProps {
  quoteLine: QuoteLineMO;
  includeQuoteLine: boolean;
  renderCheckbox?: RenderCboxFn;
  hidden?: boolean;
  alternative?: boolean;
}

export const ProductHeader = forwardRef<HTMLDivElement, ProductHeaderProps>(
  ({ quoteLine, includeQuoteLine, renderCheckbox, hidden, alternative }, ref) => {
    const productDescriptor = fakeDescriptorFromProduct(quoteLine.product);
    const { t } = useTranslation();

    const packagingLabel = getPackagingLabelAtLineItem({
      t,
      packSize: quoteLine.packSize,
      packCode: quoteLine.packCode,
      quantity: quoteLine.parts.reduce((acc, part) => acc + part.confirmedQuantity, 0),
      productDescriptor,
    });

    const quotePart = includeQuoteLine && quoteLine.parts[0];
    const confirmedQty = (quotePart: QuoteLinePartMO) =>
      quotePart.confirmedQuantity === 0 ? "-" : quotePart.confirmedQuantity;
    const quantity = (quotePart: QuoteLinePartMO) =>
      quotePart.confirmedQuantity ? quotePart.confirmedQuantity : quotePart.requestedQuantity;
    const totalPrice = (quotePart: QuoteLinePartMO) => getTotalPrice(quotePart.price, quantity(quotePart));

    if (hidden) {
      return null;
    }

    return (
      <ProductDescriptorContext value={productDescriptor}>
        <ProductHeaderContainer ref={ref}>
          {/* Product Info Cell */}
          <QuoteCell>
            <Stack gap={1}>
              <Product.DesignationContainer>
                <Product.PreferredIndicator />
                <Product.DesignationV2 variant="bodyLarge" fontWeight="bold" />
              </Product.DesignationContainer>

              <Product.MMDChip />

              {alternative && <AlternativeChip type="blue">{t("page.pdp.phaseinout.alternative.tag")}</AlternativeChip>}
              <Product.ShortDescriptionV2 />
              <SchaefflerIdParameter />
            </Stack>
          </QuoteCell>

          {/* Quantity Cell */}
          <QuoteCell>
            {quotePart && <ReverseParam label={confirmedQty(quotePart)}>{quotePart.requestedQuantity}</ReverseParam>}
          </QuoteCell>

          {/* Packaging Cell */}
          <QuoteCell>
            <Stack gap={2}>
              <ReverseParam label={packagingLabel}>{translateWarehouseName(t, quoteLine.warehouseName)}</ReverseParam>
              <AliasesParameter mode="all" packCode={quoteLine.packCode} maxAliasesVisible={99} />
            </Stack>
          </QuoteCell>

          {/* Delivery Date Cell */}
          <QuoteCell>
            {quotePart && (
              <ReverseParam label={getEDD(quotePart)}>
                {formatNullableDate(quotePart.requestedDeliveryDate, DateFormatType.DATE)}
              </ReverseParam>
            )}
          </QuoteCell>

          {/* Weight Cell */}
          <QuoteCell>
            <ReverseParam label={<WeightFormat weight={quoteLine.weight} />}>
              {t("common.product.perPiece", { count: 1 })}
            </ReverseParam>
          </QuoteCell>

          {/* Net Price Cell */}
          <QuoteCell>
            <Stack gap={2}>
              {quotePart && quotePart.price && (
                <ReverseParam
                  label={<Price price={quotePart.price} zeroText={t("product.priceOnRequest")} surchargeEnabled />}
                >
                  <Price.NetPriceUnit price={quotePart.price} variant="short" surchargeEnabled />
                </ReverseParam>
              )}
              {renderPriceReferenceParam(quoteLine, t)}
            </Stack>
          </QuoteCell>

          {/* Total Price Cell */}
          <QuoteCell>
            {quotePart && quotePart.price && (
              <Stack gap={2}>
                <ReverseParam label={<Price as="b" price={totalPrice(quotePart)} surchargeEnabled />}>
                  <Price.TotalPriceUnit price={totalPrice(quotePart)} surchargeEnabled={false} variant="short" />
                </ReverseParam>
                <IncludedSurcharge calculateFrom={totalPrice(quotePart)} />
              </Stack>
            )}
          </QuoteCell>

          {/* Status Cell */}
          <QuoteCell>{quotePart && <Chip>{t(`codes.quoteLineItemStatus.${quoteLine.status}`)}</Chip>}</QuoteCell>

          {/* Checkbox Cell */}
          <QuoteCell isCheckbox>{renderCheckbox?.(quoteLine)}</QuoteCell>
        </ProductHeaderContainer>

        {/* Messages Row */}
        <QuoteLineMessages entry={quoteLine} />
      </ProductDescriptorContext>
    );
  },
);

ProductHeader.displayName = "ProductHeader";

// ============================================================================
// Helper Functions
// ============================================================================

function renderPriceReferenceParam(quoteLine: QuoteLineMO, t: ReturnType<typeof useTranslation>["t"]) {
  if (!isPriceReferenceParamVisible() || !quoteLine.priceReference) {
    return null;
  }
  return (
    <Parameter
      label={<Trans i18nKey={getPriceReferenceLabelKey()!} />}
      value={[quoteLine.priceReference, quoteLine.priceReferenceDescription].filter(Boolean).join("-")}
    />
  );
}
